import React from 'react'
import { motion } from 'framer-motion'
import { HiArrowUp, HiArrowDown, HiXCircle, HiCheckCircle, HiPencil, HiTrash, HiCheck, HiX, HiUser, HiCash } from 'react-icons/hi'

function ChoiceItem({
  choice,
  isVoted,
  voteType,
  votingState,
  cloneMode,
  isSelected,
  onVote,
  onToggleVote,
  onRemoveVote,
  onSelect,
  maxVotes,
  // Edit mode props
  editMode,
  isEditing,
  editingText,
  setEditingText,
  isUpdating,
  canEdit,
  isOwnChoice,
  onStartEditing,
  onCancelEditing,
  onSave,
  onDelete,
  // Price/Currency props
  currency,
  currencySymbol
}) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{
        layout: {
          type: "spring",
          damping: 25,
          stiffness: 120
        },
        opacity: { duration: 0.5 },
        y: { duration: 0.5, type: "spring", damping: 25, stiffness: 120 },
        scale: { duration: 0.5, type: "spring", damping: 25, stiffness: 120 }
      }}
      className={`${isVoted ? 'choice-voted choice-voted-pulse' : ''}`}
      onClick={cloneMode && onSelect ? onSelect : undefined}
      role={cloneMode ? 'button' : undefined}
      aria-pressed={cloneMode ? isSelected : undefined}
      tabIndex={cloneMode ? 0 : undefined}
      onKeyDown={cloneMode && onSelect ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      } : undefined}
      style={{
        background: cloneMode && isSelected
          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)'
          : 'var(--glass-bg)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: cloneMode
          ? (isSelected ? '2px solid var(--accent-primary)' : '2px solid transparent')
          : (isVoted ? '2px solid #6b7280' : '1px solid var(--glass-border)'),
        borderRadius: 'var(--radius-lg)',
        padding: '0.75rem',
        marginBottom: 'var(--space-md)',
        boxShadow: cloneMode && isSelected
          ? '0 4px 20px rgba(59, 130, 246, 0.3)'
          : 'var(--shadow-md)',
        position: 'relative',
        cursor: cloneMode ? 'pointer' : 'default',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        if (cloneMode) {
          e.currentTarget.style.transform = 'translateY(-2px)'
          e.currentTarget.style.boxShadow = isSelected
            ? '0 6px 24px rgba(59, 130, 246, 0.4)'
            : '0 4px 16px rgba(59, 130, 246, 0.2)'
          if (!isSelected) {
            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)'
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.05) 100%)'
          }
        } else if (!isVoted) {
          e.currentTarget.style.transform = 'translateX(4px) translateY(-2px)'
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)'
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)'
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateX(0) translateY(0)'
        if (cloneMode) {
          e.currentTarget.style.boxShadow = isSelected
            ? '0 4px 20px rgba(59, 130, 246, 0.3)'
            : 'var(--shadow-md)'
          e.currentTarget.style.borderColor = isSelected ? 'var(--accent-primary)' : 'transparent'
          e.currentTarget.style.background = isSelected
            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)'
            : 'var(--glass-bg)'
        } else {
          e.currentTarget.style.boxShadow = 'var(--shadow-md)'
          e.currentTarget.style.borderColor = isVoted ? '#6b7280' : 'var(--glass-border)'
        }
      }}
    >
      {isVoted && (
        <div style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          color: '#9ca3af',
          fontSize: '1rem',
          animation: 'bounce-in 0.3s ease-out',
          zIndex: 10,
          pointerEvents: 'none'
        }}>
          <HiCheckCircle />
        </div>
      )}
      {cloneMode && (
        <div
          onClick={(e) => {
            e.stopPropagation()
            onSelect()
          }}
          role="checkbox"
          aria-checked={isSelected}
          aria-label={`Select ${choice.text} for export`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              onSelect()
            }
          }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '-11px',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            border: isSelected
              ? '2px solid var(--accent-primary)'
              : '2px solid var(--border-color)',
            backgroundColor: isSelected
              ? 'var(--accent-primary)'
              : 'rgba(30, 64, 120, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: isSelected
              ? '0 4px 12px rgba(59, 130, 246, 0.4)'
              : 'var(--shadow-sm)',
            animation: 'bounce-in 0.3s ease-out'
          }}
          onMouseEnter={(e) => {
            if (!isSelected) {
              e.currentTarget.style.borderColor = 'var(--accent-primary)'
              e.currentTarget.style.transform = 'scale(1.1)'
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'
            } else {
              e.currentTarget.style.transform = 'scale(1.05)'
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)'
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
            if (!isSelected) {
              e.currentTarget.style.borderColor = 'var(--border-color)'
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)'
            } else {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)'
            }
          }}
        >
          {isSelected && (
            <HiCheckCircle
              style={{
                color: 'white',
                fontSize: '14px',
                animation: 'bounce-in 0.2s ease-out',
                display: 'block'
              }}
            />
          )}
        </div>
      )}
      {/* Ownership badge - floating pill with user icon */}
      {isOwnChoice && (
        <span style={{
          position: 'absolute',
          top: '-10px',
          left: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '22px',
          height: '22px',
          color: 'var(--accent-primary)',
          background: 'rgba(30, 64, 120, 0.9)',
          borderRadius: '50%',
          zIndex: 10,
          border: '1px solid rgba(59, 130, 246, 0.3)'
        }}>
          <HiUser style={{ fontSize: '0.85rem' }} />
        </span>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', padding: '0.25rem' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* Choice Text - Left aligned */}
          {isEditing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1, minWidth: 0 }}>
              <input
                type="text"
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSave()
                  if (e.key === 'Escape') onCancelEditing()
                }}
                disabled={isUpdating}
                autoFocus
                onClick={(e) => e.stopPropagation()}
                style={{
                  flex: 1,
                  padding: '0.4rem 0.6rem',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  background: 'var(--bg-secondary)',
                  border: '2px solid var(--accent-primary)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  minWidth: '100px'
                }}
              />
              <button
                onClick={(e) => { e.stopPropagation(); onSave(); }}
                disabled={isUpdating}
                className="btn-vote-circle upvote"
                style={{ width: '28px', height: '28px', minWidth: '28px' }}
                aria-label="Save choice"
              >
                <HiCheck style={{ fontSize: '0.9rem' }} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onCancelEditing(); }}
                disabled={isUpdating}
                style={{
                  width: '28px',
                  height: '28px',
                  minWidth: '28px',
                  borderRadius: '50%',
                  border: '2px solid var(--border-color)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                aria-label="Cancel editing"
              >
                <HiX style={{ fontSize: '0.9rem' }} />
              </button>
            </div>
          ) : (
            <span style={{
              fontWeight: '600',
              fontSize: '0.9rem',
              flex: 1,
              minWidth: 0,
              wordBreak: 'break-word',
              color: isVoted ? 'var(--text-secondary)' : 'var(--text-primary)',
              lineHeight: '1.3',
              textAlign: 'left'
            }}>
              {choice.text}
              {/* Price Display */}
              {choice.price != null && (
                <span style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: 'var(--accent-secondary)',
                  marginTop: '0.25rem'
                }}>
                  {currencySymbol ? `${currencySymbol} ` : <HiCash style={{ fontSize: '0.9rem', marginRight: '0.2rem', verticalAlign: 'middle' }} />}{Number.isInteger(parseFloat(choice.price)) ? parseInt(choice.price) : parseFloat(choice.price).toFixed(2)}
                </span>
              )}
              {/* Options Display */}
              {choice.options && choice.options.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.25rem',
                  marginTop: '0.35rem'
                }}>
                  {choice.options.map((option, index) => (
                    <span
                      key={index}
                      style={{
                        display: 'inline-block',
                        padding: '0.125rem 0.4rem',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        background: 'rgba(139, 92, 246, 0.15)',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                        borderRadius: 'var(--radius-full)',
                        color: 'var(--text-secondary)'
                      }}
                    >
                      {option}
                    </span>
                  ))}
                </div>
              )}
            </span>
          )}

          {/* Edit mode buttons */}
          {editMode && canEdit && !isEditing && (
            <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
              <button
                onClick={(e) => { e.stopPropagation(); onStartEditing(); }}
                disabled={isUpdating}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--accent-primary)'
                  e.currentTarget.style.color = 'var(--accent-primary)'
                  e.currentTarget.style.transform = 'scale(1.1)'
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(59, 130, 246, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)'
                  e.currentTarget.style.color = 'var(--text-secondary)'
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
                style={{
                  width: '28px',
                  height: '28px',
                  minWidth: '28px',
                  borderRadius: '50%',
                  border: '2px solid var(--border-color)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                aria-label="Edit choice"
              >
                <HiPencil style={{ fontSize: '0.8rem' }} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this choice?')) onDelete(); }}
                disabled={isUpdating}
                className="btn-vote-circle downvote"
                style={{ width: '28px', height: '28px', minWidth: '28px' }}
                aria-label="Delete choice"
              >
                <HiTrash style={{ fontSize: '0.8rem' }} />
              </button>
            </div>
          )}

          {/* Vote count pill - highlighted background */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.25rem 0.5rem',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: 'var(--radius-full)',
            flexShrink: 0
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.1rem', color: 'var(--success-color)', fontWeight: '600', fontSize: '0.85rem' }}>
              <HiArrowUp style={{ fontSize: '0.85rem' }} />
              {choice.upvote_count || 0}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.1rem', color: 'var(--error-color)', fontWeight: '600', fontSize: '0.85rem' }}>
              <HiArrowDown style={{ fontSize: '0.85rem' }} />
              {choice.downvote_count || 0}
            </span>
          </div>

          {/* Vote buttons - Circular outline style */}
          {!isVoted ? (
            <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
              <button
                className={`btn-vote-circle upvote ${votingState === 'upvote' ? 'btn-loading' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onVote(choice.id, 'upvote')
                }}
                disabled={votingState}
                aria-label={`Upvote ${choice.text}`}
              >
                <HiArrowUp style={{ fontSize: '1rem' }} />
              </button>
              <button
                className={`btn-vote-circle downvote ${votingState === 'downvote' ? 'btn-loading' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onVote(choice.id, 'downvote')
                }}
                disabled={votingState}
                aria-label={`Downvote ${choice.text}`}
              >
                <HiArrowDown style={{ fontSize: '1rem' }} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
              <button
                className={`btn-vote-circle ${voteType === 'upvote' ? 'downvote' : 'upvote'} ${votingState === 'upvote' || votingState === 'downvote' ? 'btn-loading' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleVote(choice.id)
                }}
                disabled={votingState}
                aria-label={`Toggle vote for ${choice.text}`}
              >
                {voteType === 'upvote' ? (
                  <HiArrowDown style={{ fontSize: '1rem' }} />
                ) : (
                  <HiArrowUp style={{ fontSize: '1rem' }} />
                )}
              </button>
              <button
                className={`btn-vote-circle ${votingState === 'remove' ? 'btn-loading' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onRemoveVote(choice.id)
                }}
                disabled={votingState}
                style={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  padding: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: '2px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-base)'
                }}
                aria-label={`Remove vote for ${choice.text}`}
              >
                <HiXCircle style={{ fontSize: '1rem' }} />
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div >
  )
}

export default React.memo(ChoiceItem)
